import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Target, RefreshCw, Plus, Trash2, ExternalLink, X, Sparkles,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function LeadFinder() {
  const [signals, setSignals] = useState([]);
  const [searches, setSearches] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [runBusy, setRunBusy] = useState(null);
  const [enrichBusy, setEnrichBusy] = useState(null);
  const [form, setForm] = useState({
    label: "", city: "", state: "",
    signals: ["stale_90", "price_reduced"],
    auto_refresh: true,
  });

  const load = async () => {
    const [{ data: sig }, { data: sx }] = await Promise.all([
      api.get("/lead-finder/signals"),
      api.get("/lead-finder/searches"),
    ]);
    setSignals(sig.signals || []);
    setSearches(sx || []);
    if (sx && sx.length && !activeId) setActiveId(sx[0].id);
  };

  const loadMatches = async (sid) => {
    if (!sid) {
      setMatches([]);
      return;
    }
    const { data } = await api.get("/lead-finder/matches", {
      params: { search_id: sid, status: "new" },
    });
    setMatches(data.items || []);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadMatches(activeId); }, [activeId]);

  const toggleSignal = (k) => {
    setForm((f) => ({
      ...f,
      signals: f.signals.includes(k)
        ? f.signals.filter((b) => b !== k)
        : [...f.signals, k],
    }));
  };

  const createSearch = async () => {
    if (!form.label.trim() || !form.city.trim() || !form.state.trim()) {
      toast.error("Label, city, and state are required");
      return;
    }
    if (form.signals.length === 0) {
      toast.error("Pick at least one motivation signal");
      return;
    }
    try {
      const { data } = await api.post("/lead-finder/searches", {
        label: form.label.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        signals: form.signals,
        auto_refresh: form.auto_refresh,
      });
      toast.success("Search saved — running first scan…");
      setDialogOpen(false);
      setForm({
        label: "", city: "", state: "",
        signals: ["stale_90", "price_reduced"],
        auto_refresh: true,
      });
      await load();
      setActiveId(data.id);
      runSearch(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const runSearch = async (sid) => {
    setRunBusy(sid);
    try {
      const { data } = await api.post(`/lead-finder/searches/${sid}/run`);
      toast.success(`Found ${data.new_count} new match${data.new_count === 1 ? "" : "es"}`);
      await load();
      if (activeId === sid) await loadMatches(sid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRunBusy(null);
    }
  };

  const deleteSearch = async (sid) => {
    if (!window.confirm("Delete this saved search and all its matches?")) return;
    await api.delete(`/lead-finder/searches/${sid}`);
    if (activeId === sid) setActiveId(null);
    load();
  };

  const dismissMatch = async (mid) => {
    await api.post(`/lead-finder/matches/${mid}/dismiss`);
    setMatches((m) => m.filter((x) => x.id !== mid));
  };

  const enrichKeywords = async (mid) => {
    setEnrichBusy(mid);
    try {
      const { data } = await api.post(`/lead-finder/matches/${mid}/enrich-keywords`);
      setMatches((prev) => prev.map((m) => (m.id === mid ? {
        ...m,
        keyword_hits: data.hits,
        description_excerpt: data.excerpt,
        enriched_at: new Date().toISOString(),
      } : m)));
      if (data.hits && data.hits.length) {
        toast.success(`Found ${data.hits.length} keyword${data.hits.length === 1 ? "" : "s"}`);
      } else if (data.text_available) {
        toast.info("No keywords matched in the listing description");
      } else {
        toast.info("No description available for this listing");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnrichBusy(null);
    }
  };

  const convertMatch = async (mid) => {
    try {
      await api.post(`/lead-finder/matches/${mid}/convert`);
      toast.success("Added to CRM Pipeline");
      setMatches((m) => m.filter((x) => x.id !== mid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    }
  };

  const active = searches.find((s) => s.id === activeId);

  return (
    <div className="p-6 md:p-10" data-testid="lead-finder-page">
      <div className="flex items-end justify-between mb-6 reveal flex-wrap gap-3">
        <div>
          <span className="app-eyebrow">/ Lead discovery</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Lead Finder</h1>
          <p className="text-[var(--app-text-muted)] text-sm mt-2 max-w-lg">
            Save a city + motivation signals. FlipPilot scans active listings
            daily and flags the ones showing objective seller motivation:
            stale, price-reduced, relisted, or vacant land.
          </p>
        </div>
        <button
          onClick={() => setDialogOpen(true)}
          data-testid="new-search-button"
          className="app-btn app-btn-primary"
        >
          <Plus size={14} /> New search
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        <div className="space-y-2" data-testid="saved-searches">
          {searches.length === 0 && (
            <div className="card-flat p-6 text-sm text-[var(--app-text-muted)] text-center">
              <Target size={22} className="mx-auto mb-2 opacity-40" />
              No saved searches yet.
            </div>
          )}
          {searches.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              className={`card-flat p-4 w-full text-left transition-colors ${
                activeId === s.id
                  ? "border-[var(--app-accent)] bg-[var(--app-surface)]"
                  : ""
              }`}
              data-testid={`search-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.label}</div>
                  <div className="text-[11px] text-[var(--app-text-muted)] mt-0.5 truncate">
                    {s.city}, {s.state}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="kpi-num text-lg leading-none">
                    {s.last_result_count}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-[var(--app-text-muted)]">
                    new
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="text-[10px] text-[var(--app-text-muted)] flex-1">
                  refreshed {timeAgo(s.last_run_at)}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); runSearch(s.id); }}
                  disabled={runBusy === s.id}
                  className="text-[var(--app-text-muted)] hover:text-[var(--app-accent-dark)] disabled:opacity-50"
                  data-testid={`refresh-${s.id}`}
                  title="Refresh now"
                >
                  <RefreshCw
                    size={12}
                    className={runBusy === s.id ? "animate-spin" : ""}
                  />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSearch(s.id); }}
                  className="text-[var(--app-text-muted)] hover:text-red-600"
                  data-testid={`delete-search-${s.id}`}
                  title="Delete search"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              {s.last_error && (
                <div className="text-[10px] text-red-600 mt-1 truncate">
                  {s.last_error}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="card-flat overflow-hidden" data-testid="matches-panel">
          {!active && (
            <div className="p-16 text-center text-[var(--app-text-muted)]">
              <Sparkles size={22} className="mx-auto mb-2 opacity-40" />
              Select or create a search to see matched listings.
            </div>
          )}
          {active && (
            <>
              <div className="p-5 border-b border-[var(--app-line)] flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display text-lg">
                    {active.label} — {active.city}, {active.state}
                  </div>
                  <div className="text-xs text-[var(--app-text-muted)] mt-0.5">
                    {matches.length} new match{matches.length === 1 ? "" : "es"} ·
                    refreshed {timeAgo(active.last_run_at)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(active.signals || []).map((b) => {
                    const s = signals.find((x) => x.key === b);
                    return (
                      <span key={b} className="app-chip text-[10px]">
                        {s?.label || b}
                      </span>
                    );
                  })}
                </div>
              </div>

              {matches.length === 0 && (
                <div className="p-16 text-center text-[var(--app-text-muted)]">
                  No matches yet.{" "}
                  <button
                    onClick={() => runSearch(active.id)}
                    className="text-[var(--app-accent-dark)] hover:underline"
                  >
                    Refresh
                  </button>{" "}
                  to scan the latest active listings.
                </div>
              )}

              {matches.map((m) => (
                <div
                  key={m.id}
                  className="p-5 border-b border-[var(--app-line)] hover:bg-[var(--app-surface)]/60"
                  data-testid={`match-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <a
                        href={`https://www.zillow.com/homes/${encodeURIComponent(m.address)}_rb/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-[var(--app-accent-dark)] hover:text-[var(--app-accent)] hover:underline inline-flex items-center gap-1"
                        data-testid={`match-address-${m.id}`}
                      >
                        {m.address}
                        <ExternalLink size={11} className="opacity-70" />
                      </a>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--app-text-muted)]">
                        <span className="font-mono text-[var(--app-text)]">
                          {money(m.price)}
                        </span>
                        {m.property_type && <span>{m.property_type}</span>}
                        {m.bedrooms ? <span>{m.bedrooms}bd</span> : null}
                        {m.bathrooms ? <span>{m.bathrooms}ba</span> : null}
                        {m.sqft ? <span>{m.sqft} sqft</span> : null}
                        {m.year_built ? <span>built {m.year_built}</span> : null}
                        {m.days_on_market != null && (
                          <span className={
                            m.days_on_market >= 180
                              ? "text-red-600 font-semibold"
                              : m.days_on_market >= 90 ? "text-amber-700" : ""
                          }>
                            {m.days_on_market} DOM
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(m.matched_signals || []).map((k, i) => (
                          <span
                            key={i}
                            className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-red-50 text-red-700"
                          >
                            {k}
                          </span>
                        ))}
                      </div>
                      {m.last_price_drop && (
                        <div className="mt-2 text-[11px] text-[var(--app-text-muted)]">
                          Last price drop {money(m.last_price_drop.delta)} —
                          {" "}{money(m.last_price_drop.from)} → {money(m.last_price_drop.to)}
                          {" "}on {new Date(m.last_price_drop.date).toLocaleDateString()}
                        </div>
                      )}
                      {m.keyword_hits && m.keyword_hits.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1" data-testid={`keywords-${m.id}`}>
                          {m.keyword_hits.map((k, i) => (
                            <span
                              key={i}
                              className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200"
                            >
                              🔥 {k}
                            </span>
                          ))}
                        </div>
                      )}
                      {m.description_excerpt && (
                        <div className="mt-2 text-[11px] italic text-[var(--app-text-muted)] leading-relaxed">
                          &ldquo;{m.description_excerpt}&rdquo;
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => convertMatch(m.id)}
                        className="app-btn app-btn-primary h-8 text-xs px-3"
                        data-testid={`convert-${m.id}`}
                      >
                        Add as lead
                      </button>
                      {!m.enriched_at && (
                        <button
                          onClick={() => enrichKeywords(m.id)}
                          disabled={enrichBusy === m.id}
                          className="text-[10px] uppercase tracking-widest text-[var(--app-accent-dark)] hover:text-[var(--app-accent)] disabled:opacity-40 py-1"
                          data-testid={`enrich-${m.id}`}
                          title="Fetch listing description and scan for keywords (costs 1 RentCast credit)"
                        >
                          {enrichBusy === m.id ? "Scanning…" : "🔎 Scan keywords (1cr)"}
                        </button>
                      )}
                      <button
                        onClick={() => dismissMatch(m.id)}
                        className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] hover:text-red-600 py-1"
                        data-testid={`dismiss-${m.id}`}
                      >
                        <X size={11} className="inline" /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="rounded-2xl max-w-lg"
          data-testid="new-search-dialog"
        >
          <DialogHeader>
            <DialogTitle>New motivation search</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label</Label>
              <Input
                data-testid="search-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Cape Coral motivated"
                className="field-sharp mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label>City</Label>
                <Input
                  data-testid="search-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Cape Coral"
                  className="field-sharp mt-1"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  data-testid="search-state"
                  maxLength={2}
                  value={form.state}
                  onChange={(e) =>
                    setForm({ ...form, state: e.target.value.toUpperCase() })
                  }
                  placeholder="FL"
                  className="field-sharp mt-1 uppercase font-mono"
                />
              </div>
            </div>
            <div>
              <Label>Motivation signals</Label>
              <div className="mt-2 space-y-1.5">
                {signals.map((s) => {
                  const selected = form.signals.includes(s.key);
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => toggleSignal(s.key)}
                      data-testid={`signal-${s.key}`}
                      className={`w-full text-left px-3 py-2 rounded-xl border transition-colors ${
                        selected
                          ? "bg-[var(--app-black)] text-white border-[var(--app-black)]"
                          : "bg-white text-[var(--app-text)] border-[var(--app-line)] hover:border-[var(--app-black)]"
                      }`}
                    >
                      <div className="text-sm font-medium">{s.label}</div>
                      <div className={`text-[11px] ${
                        selected ? "text-white/60" : "text-[var(--app-text-muted)]"
                      }`}>
                        {s.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_refresh}
                onChange={(e) =>
                  setForm({ ...form, auto_refresh: e.target.checked })
                }
                data-testid="auto-refresh"
              />
              Auto-refresh daily
            </label>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="app-btn app-btn-ghost"
              data-testid="cancel-search"
            >
              Cancel
            </button>
            <button
              onClick={createSearch}
              className="app-btn app-btn-primary"
              data-testid="save-search"
            >
              Save & scan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
